import type { Theme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import { useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useAuthStore, usePortalStore } from "../../stores/store";
import type { AuthenStoreType } from "../../types/authen";
import type { InitialUserPortalType } from "../../types/global";
import {
  BackofficeDutyProvider,
  RequireDuty,
} from "../auth/BackofficeDutyProvider";
import { resolveDutiesFromBackendDuties } from "../auth/resolveDutiesFromToken";
import { bolonComponentOverrides } from "./theme";

/*
 * The BOLON area inside the host app (migration D6, D10).
 *
 * Rendered as a pathless layout route under the host's member `User` layout,
 * so the app bar, drawer and breadcrumbs are the host's. What this adds:
 *
 *  - the duty context every RequireDuty/useHasDuty gate reads, resolved from
 *    the backend duties the host login stored;
 *  - the gate on "bolon" itself — a member with no bolon_* duty sees Access
 *    denied here instead of a half-working shell;
 *  - the BOLON component overrides on top of the host theme, for /bolon/* only.
 *
 * Spread, not createTheme(): the host theme is a CSS-variables theme and
 * re-running createTheme over a built theme would rebuild it without them.
 * Only `components` changes.
 */
const withBolonOverrides = (outer: Theme): Theme => ({
  ...outer,
  components: deepmerge(
    outer.components ?? {},
    bolonComponentOverrides,
  ) as Theme["components"],
});

export function BolonLayout() {
  const { user } = useAuthStore((state) => state as AuthenStoreType);
  const { portal, setPortalState } = usePortalStore(
    (state) => state as InitialUserPortalType,
  );

  // The host drawer picks its menu from the persisted portal value. A deep
  // link into /bolon/* must show the Bolon menu even when the last portal
  // used was KEYIN.
  useEffect(() => {
    if (portal !== "BOLON") setPortalState("BOLON");
  }, [portal, setPortalState]);

  // Bolon pages draw their own breadcrumbs inline and never write the host's
  // breadcrumb context, so whatever the last host page set ("Tasks") stayed
  // on screen above a Bolon page after in-app navigation. Clear it on every
  // Bolon route; CustomBreadcrumbs renders nothing for an empty list.
  const { setBreadcrumbs } = useBreadcrumbs();
  const { pathname } = useLocation();
  useEffect(() => {
    setBreadcrumbs([]);
  }, [pathname, setBreadcrumbs]);

  const duties = useMemo(
    () => resolveDutiesFromBackendDuties(user?.duties),
    [user?.duties],
  );

  return (
    <BackofficeDutyProvider duties={duties}>
      <ThemeProvider theme={withBolonOverrides}>
        <RequireDuty duty="bolon">
          <Outlet />
        </RequireDuty>
      </ThemeProvider>
    </BackofficeDutyProvider>
  );
}
